import React, { useEffect, useRef } from 'react';

// 旧 styles/components/modal/Modal.scss を内製化（Wave 3d / T20）。
// 実効値は Modal.scss 単体の値ではなく、ProductSelectionModal.scss /
// ProductImageSelectionModal.scss が bare な `.modal-*` をトップレベルで
// 再定義して上書きしていたマージ後の値（border-bottom は #e5e7eb、
// padding/align は Wave 3c の補填クラスが優先）。

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);

  // ESCキーを押したときに閉じる & body のスクロール制御
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      // body のスクロールを無効化
      document.body.style.overflow = 'hidden';
      // モーダルコンテンツを最上部にスクロール
      if (modalContentRef.current) {
        modalContentRef.current.scrollTop = 0;
      }
    } else {
      // body のスクロールを復元
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      // クリーンアップ時にスクロールを復元
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // モーダルの外側をクリックしたときに閉じる
  const handleOutsideClick = (event: React.MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="modal-overlay fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-5 max-[768px]:p-2.5"
      onClick={handleOutsideClick}
    >
      <div
        className="relative flex max-h-[85vh] w-full max-w-[800px] flex-col overflow-hidden rounded-lg bg-white shadow-[0_10px_25px_rgba(0,0,0,0.3)] max-[768px]:max-h-[95vh] max-[768px]:max-w-none"
        ref={modalRef}
      >
        <div className="modal-header flex shrink-0 items-start justify-between rounded-t-lg border-b border-b-gray-200 bg-[#fafafa] px-6 py-5 max-[768px]:p-4">
          <h2 className="text-[1.3rem] font-semibold text-[#333] max-[768px]:text-[1.2rem]">{title}</h2>
          <button
            className="ml-3 cursor-pointer rounded border-none bg-transparent px-2 py-1 text-[1.8rem] leading-none text-[#666] transition-all duration-200 ease-[ease] hover:bg-[#eee] hover:text-black"
            onClick={onClose}
            aria-label="閉じる"
          >
            &times;
          </button>
        </div>
        <div className="modal-content flex min-h-0 w-full max-w-full flex-1 flex-col overflow-y-auto overflow-x-hidden p-6 [scrollbar-color:#c1c1c1_#f1f1f1] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb:hover]:bg-[#a8a8a8] [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-[#c1c1c1] [&::-webkit-scrollbar-track]:rounded [&::-webkit-scrollbar-track]:bg-[#f1f1f1] [&::-webkit-scrollbar]:w-2 max-[768px]:p-4">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;
